import {
  SEL_DEFAULT_SUBJECT_STRATEGIES_GOALS,
  SEL_REPLACEMENT_GOALS,
  getGoalSubjectLabelFromValue
} from '@/contentprofile/no_sel_composable'
import { useVisibilityStore } from '@/visibility/visibility.store'

export interface AssessmentSubjectTaxonomy {
  subject: string
  categories: string[]
}

// The subjects and categories Browse Assessments offers, in display order. Algolia only ever
// upserts, so retired subject and category names stay in the index; this list is what hides
// them. It is one of several copies of the taxonomy that must change together, alongside
// toolkit-spa's assessments_filter_options.js and the PLW2 BrowseAssessmentsSubjects and
// BrowseAssessmentsDomains Airtable sheets — see .claude/skills/update-subject-taxonomy.
export const ASSESSMENT_TAXONOMY: AssessmentSubjectTaxonomy[] = [
  {
    subject: 'Reading',
    categories: ['Concepts of Print', 'High Frequency Word Reading', 'Phonics & Decoding', 'Phonological Awareness', 'Fluency', 'Comprehension', 'Language Structures', 'Vocabulary']
  },
  {
    subject: 'Writing',
    categories: ['Letter Formation', 'Organization of Ideas', 'Conventions & Mechanics', 'Informative & Explanatory']
  },
  {
    subject: 'Math',
    categories: ['Early Numeracy', 'Number System', 'Number Operations', 'Measurement & Data', 'Geometry', 'Expressions & Equations', 'Ratios & Proportions', 'Statistics & Probability', 'Functions', 'Financial Literacy']
  },
  { subject: 'Science', categories: [] },
  { subject: 'Social Studies', categories: [] },
  {
    subject: SEL_DEFAULT_SUBJECT_STRATEGIES_GOALS,
    categories: ['Self-Awareness', 'Self-Management', 'Social Awareness', 'Relationship Skills', 'Responsible Decision Making', 'Behavior Data Collection']
  },
  {
    subject: 'Pre-Academics',
    categories: ['Cognitive Skills', 'Play Skills', 'Motor Skills', 'Emotional Development', 'Early Numeracy', 'Early Literacy', 'Communication & Language']
  },
  {
    subject: 'Speech & Language',
    categories: ['Fluency', 'Phonological Processes', 'Articulation', 'Receptive Language', 'Expressive Language', 'Pragmatics']
  },
  {
    subject: 'Transition',
    categories: ['Education & Training', 'Employment', 'Independent Living', 'Self Determination']
  },
  {
    subject: 'Life Skills',
    categories: ['Community Skills', 'Self-Advocacy', 'Personal Care', 'Household Skills', 'Financial Literacy', 'Transportation & Travel', 'Employment', 'Orientation & Mobility']
  },
  {
    subject: 'Alt Academics: Reading',
    categories: ['Concepts of Print', 'Phonological Awareness', 'Letter/Sound Identification', 'High Frequency & Functional Word Reading', 'Phonics & Decoding', 'Fluency', 'Comprehension']
  },
  {
    subject: 'Alt Academics: Writing',
    categories: ['Letter Formation', 'Spelling', 'Capitalization & Punctuation', 'Organization of Ideas', 'Writing Fluency']
  },
  {
    subject: 'Alt Academics: Math',
    categories: ['Early Numeracy', 'Number Sense & Concepts', 'Calculation & Computation', 'Geometry & Measurement', 'Functional Math']
  },
  {
    subject: 'Executive Functioning',
    categories: ['Cognitive Flexibility', 'Working Memory', 'Organization & Planning', 'Self-Monitoring']
  },
  {
    subject: 'Fine Motor',
    categories: ['Fine Motor Precision', 'Functional Fine Motor', 'Handwriting', 'Visual Motor', 'Visual Perception', 'Sensory Integration', 'Keyboarding']
  },
  {
    subject: 'Gross Motor',
    categories: ['Balance & Coordination', 'Locomotor Skills', 'Strength', 'Functional Mobility', 'Foundational Movement & Transfers', 'Object Control']
  },
  {
    subject: 'Assistive Technology',
    categories: ['Augmentative & Alternative Communication', 'Access & Operation', 'Daily Routine Integration', 'Self-Advocacy', 'Troubleshooting']
  },
  {
    subject: 'English Language Development',
    categories: ['Reading', 'Writing', 'Listening', 'Speaking']
  }
]

const ORIENTATION_MOBILITY = 'Orientation & Mobility'

const CORE_SUBJECTS = ['Reading', 'Writing', 'Math']

export function isCoreSubject(subject: string | undefined): boolean {
  return CORE_SUBJECTS.includes(subject ?? '')
}

// A no-SEL content profile sees Behavior & SEL under a replacement name. The index tags
// those assessments with the replacement name too, so the name is also the filter value.
export function assessmentSubjectsForProfile(): string[] {
  return ASSESSMENT_TAXONOMY.map(({ subject }) => getGoalSubjectLabelFromValue(subject))
}

function taxonomySubjectFor(subject: string): string {
  const isSelReplacementName = SEL_REPLACEMENT_GOALS.includes(subject)
  if (isSelReplacementName) {
    return SEL_DEFAULT_SUBJECT_STRATEGIES_GOALS
  }
  return subject
}

// Staff switch Orientation & Mobility on in Visibility Settings; everyone else never sees it.
function isCategoryShown(category: string): boolean {
  return category !== ORIENTATION_MOBILITY || useVisibilityStore().orientationMobilityVisibility
}

export function assessmentCategoriesFor(subject: string | undefined): string[] {
  if (!subject) {
    return []
  }
  const taxonomyEntry = ASSESSMENT_TAXONOMY.find((entry) => entry.subject === taxonomySubjectFor(subject))
  const categories = taxonomyEntry?.categories ?? []
  return categories.filter(isCategoryShown)
}
